import { toSignal } from '@angular/core/rxjs-interop';
import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ButtonComponent } from '@shared/components/button';
import { FieldErrorDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { PillComponent } from '@shared/components/pill';
import { SelectComponent } from '@shared/components/select';
import { SegmentedControlComponent } from '@shared/components/segmented-control';
import { TooltipDirective } from '@shared/directives/tooltip';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';
import type { SegmentOption } from '@shared/components/segmented-control';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { AjoutParametragesSpecifiquesPackRequetePayload, Pack } from '../../../commissions.interface.';
import { CustomValidators } from '../../../../../../partage/forms/custom-validators';
import { normaliserSaisieMontant, normaliserSaisieNombre } from '../../../../../../partage/forms/normalisation-saisie';
import { nombreDepuisSaisie, nombreDepuisSaisieOuRepli } from '../../../../../../partage/format/nombre';
import { formaterDevise } from '../../../../../../partage/format/montant';
import { formaterPourcentage } from '../../../../../../partage/format/pourcentage';
import { CommissionsService } from '../../../commissions.service';

@Component({
  selector: 'app-ajouter-commission-pack',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    FieldErrorDirective,
    FormFieldComponent,
    InputComponent,
    PillComponent,
    SelectComponent,
    SegmentedControlComponent,
    TooltipDirective,
  ],
  templateUrl: './ajouter-commission-pack.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './ajouter-commission-pack.component.scss',
})
export class AjouterCommissionPackComponent {
  private commissionsService = inject(CommissionsService);
  packsSelectionnes = new FormControl<Pack[]>([]);

  voirPanel = this.commissionsService.voirPanelAjoutParametrageSpecifiquePack;
  packs = this.commissionsService.packs;

  reglesForm = new FormGroup({
    regle: new FormGroup({
      // Le POURCENTAGE est borné à 100, le montant ne l'est pas : en pratique il ne
      // dépasse jamais 100 € mais rien ne l'interdit. Les deux contraintes étaient
      // posées sur le montant, et aucune sur le pourcentage.
      // `null` et non `0` : « rien saisi » doit rester représentable. Initialisés à 0,
      // un simple focus + sortie du champ faisait apparaître l'aperçu — la
      // renormalisation réécrivait `0` (falsy) en `'0,00'` (truthy) sans aucune
      // saisie (review Gildas, MR !29). Le payload ne change pas :
      // `nombreDepuisSaisieOuRepli(null)` rend 0, et les validateurs laissent
      // passer le vide (`estVide`).
      pourcentage: new FormControl<number | null>(null, [
        CustomValidators.minNombre(0),
        CustomValidators.maxNombre(100),
      ]),
      minimum: new FormControl<number | null>(null, [CustomValidators.minNombre(0)]),
    }),
    parametragePourLesPacksEstGlobal: new FormControl<boolean>(true),
  });

  // Miroirs signal du formulaire de règle, lus par le gabarit et l'aperçu
  protected readonly regles = toSignal(this.reglesForm.valueChanges, { initialValue: this.reglesForm.value });
  protected readonly reglesValides = toSignal(this.reglesForm.statusChanges.pipe(map(() => this.reglesForm.valid)), {
    initialValue: this.reglesForm.valid,
  });

  constructor() {
    effect(() => {
      if (this.voirPanel()) {
        this.commissionsService.recupererLesPacksPourParametrage().subscribe();
      }
    });
  }

  // `InputType` du kit ne propose pas `number` : la saisie numérique passe par
  // `inputMode` + un filtre de caractères (voir design-system-manques.md, A14)
  protected readonly chiffres = /[0-9.,]/;

  // `app-select` raisonne en clés : `idFormule` sert de clé, et les états connus
  // passent en suffixe d'option (Material les rendait en `mat-chip`)
  protected optionsPacks = computed<SelectOption[]>(() =>
    (this.packs() ?? []).map(element => {
      const etats = [
        element.estVerrouille ? 'acompte fait' : null,
        element.parametrageSpecifique ? 'déjà paramétré' : null,
      ].filter(Boolean);

      return {
        value: String(element.idFormule),
        label: `${element.idAparte} - ${element.nom}`,
        ...(etats.length ? { suffix: etats.join(' · ') } : {}),
      };
    }),
  );

  /* Miroir signal de la selection. Un `computed` lisant `packsSelectionnes.value`
     ne se recalculait jamais — un `FormControl` n'est pas un signal : la liste
     deroulante ne recevait donc plus l'etat coche et une seule entree semblait
     selectionnable. */
  protected readonly clesPacksSelectionnesSource = toSignal(this.packsSelectionnes.valueChanges, {
    initialValue: this.packsSelectionnes.value,
  });

  protected clesPacksSelectionnes = computed<string[]>(() =>
    (this.clesPacksSelectionnesSource() ?? []).map(element => String(element.idFormule)),
  );

  protected choisirPacks(cles: readonly string[]): void {
    const retenus = (this.packs() ?? []).filter(element => cles.includes(String(element.idFormule)));
    this.packsSelectionnes.setValue(retenus);
  }

  // Choix binaire : `radiogroup` du kit est un helper de comportement, pas un
  // composant — `app-segmented-control` est le rendu prévu pour ce cas
  protected readonly optionsPortee: SegmentOption<boolean>[] = [
    { value: true, kind: 'text', label: 'Global' },
    { value: false, kind: 'text', label: 'Détails' },
  ];

  protected porteeGlobale = signal<boolean>(true);

  protected changerPortee(globale: boolean): void {
    this.porteeGlobale.set(globale);
    this.reglesForm.controls.parametragePourLesPacksEstGlobal.setValue(globale);
  }

  retirerPackDeLaSelection(event: Pack) {
    const currentEvents = this.packsSelectionnes.value ?? [];
    const updatedEvents = currentEvents.filter(e => e.idFormule !== event.idFormule);
    this.packsSelectionnes.setValue(updatedEvents);
  }

  /* Renormalisation a la sortie du champ. Le TAUX ne doit pas etre arrondi (il part
     tel quel au serveur), le MONTANT est borne a deux decimales : deux fonctions. */
  protected readonly normaliserTaux = normaliserSaisieNombre;
  protected readonly normaliserMontant = normaliserSaisieMontant;

  readonly reglePourPackLibelle = computed(() => {
    const regle = this.regles().regle;
    const minimum = formaterDevise(nombreDepuisSaisie(regle?.minimum));

    /* Test NUMERIQUE, pas truthy : le kit pousse des CHAINES et `"0"` est truthy —
       un taux saisi a 0 doit dire « (fixe) », comme la liste (`pourcentage > 0`). */
    if (nombreDepuisSaisieOuRepli(regle?.pourcentage) > 0) {
      const pourcentage = formaterPourcentage(nombreDepuisSaisie(regle?.pourcentage));
      return `Pour chaque pack vendu, la commission web Tick&Live est de ${pourcentage} avec un minimum de ${minimum} `;
    }

    return `Pour chaque pack vendu, la commission web Tick&Live est de ${minimum} (fixe)`;
  });

  fermerSlider() {
    this.voirPanel.set(false);
  }

  /* Un `FormControl` n'est pas un signal : sans ce miroir, la ligne d'erreur ne
     réapparaîtrait pas à la frappe. Le `Valider` se contente d'un `[disabled]`, une
     saisie hors bornes serait sinon refusée sans que rien ne l'explique. */
  private readonly pourcentageSaisi = toSignal(this.reglesForm.controls.regle.controls.pourcentage.valueChanges, {
    initialValue: this.reglesForm.controls.regle.controls.pourcentage.value,
  });

  protected pourcentageHorsBornes = computed(() => {
    this.pourcentageSaisi();
    return this.reglesForm.controls.regle.controls.pourcentage.invalid;
  });

  valider() {
    if (this.reglesForm.valid && this.packsSelectionnes?.value?.length) {
      const packIds = this.packsSelectionnes.value.map(x => x.idFormule);
      const { regle } = this.reglesForm.value;

      // pourcentage et minimum à 0 pour les produits car pas geré pour le moment
      const payload: AjoutParametragesSpecifiquesPackRequetePayload = {
        idsFormules: packIds,
        regle: {
          minimum: nombreDepuisSaisieOuRepli(regle?.minimum),
          pourcentage: nombreDepuisSaisieOuRepli(regle?.pourcentage),
        },
        estUnParametrageGlobal: this.reglesForm.value.parametragePourLesPacksEstGlobal!,
      };

      this.commissionsService.parametrageSpecifiquePackAAjouter.set(payload);
      this.fermerSlider();
    }
  }
}
